import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, X, Plus, Upload, Trash2, ChevronDown, ChevronRight } from 'lucide-react';
import axiosInstance from '../../services/api/axios';
import { toast } from 'react-toastify';
import { PrimaryButton, OutlineButton } from '../../components/Buttons';
import CustomInput from '../../components/CustomInput';
import LoadingImage from '../../components/LoadingImage';
import { getPresignedUploadUrls, getPresignedDownloadUrls, getPresignedDownloadUrl, uploadFilesToS3 } from '../../utils/fileUtils';
import { HOUSE_SECTIONS } from '../../constants/houseSections';
import { designsPortfolioReturnHref } from './designsRoute';

/** Normalize API designMetadata into the shape used by this form */
function normalizeDesignMetadataForForm(raw) {
  const empty = {
    living_room: {},
    bedrooms: [{ bathrooms: [] }],
    kitchen: {},
    bathrooms: [],
  };
  if (!raw || typeof raw !== 'object') return empty;
  return {
    living_room: { ...(raw.living_room || {}) },
    bedrooms:
      Array.isArray(raw.bedrooms) && raw.bedrooms.length > 0
        ? raw.bedrooms.map((b) => ({
            ...(b && typeof b === 'object' ? b : {}),
            bathrooms: Array.isArray(b?.bathrooms)
              ? b.bathrooms.map((bb) => ({ ...(bb && typeof bb === 'object' ? bb : {}) }))
              : [],
          }))
        : [{ bathrooms: [] }],
    kitchen: { ...(raw.kitchen || {}) },
    bathrooms: Array.isArray(raw.bathrooms)
      ? raw.bathrooms.map((b) => ({ ...(b && typeof b === 'object' ? b : {}) }))
      : [],
  };
}

function extractNumericIds(arr) {
  if (!arr || !Array.isArray(arr)) return [];
  if (arr.length > 0 && typeof arr[0] === 'object') {
    return arr
      .map((x) => x?.id ?? x?.designThemeId ?? x?.designSubTagId ?? x?.design_theme_id ?? x?.design_sub_tag_id)
      .filter((id) => id != null)
      .map(Number);
  }
  return arr.map((x) => Number(x)).filter((n) => !Number.isNaN(n));
}

const AddPortfolio = () => {
  const navigate = useNavigate();
  const { portfolioId: editPortfolioIdParam } = useParams();
  const isEditMode = Boolean(editPortfolioIdParam);
  const pageTitle = isEditMode ? 'Edit Portfolio Item' : 'Add Portfolio Item';
  const pageSubtitle = isEditMode
    ? 'Update project details, design metadata, and BOQ'
    : 'Create a new portfolio item';
  const [loading, setLoading] = useState(false);
  const [editInitialLoading, setEditInitialLoading] = useState(false);
  // True while portfolio images are being uploaded to S3 (on selection).
  // Submit is disabled until this clears so we never POST a half-uploaded set.
  const [uploadingImages, setUploadingImages] = useState(false);
  // Design metadata room sub-stepper
  const [selectedRoomType, setSelectedRoomType] = useState('living_room');
  // Store portfolio ID after creation to allow navigation to BOQ
  const [createdPortfolioId, setCreatedPortfolioId] = useState(null);
  const [currentStep, setCurrentStep] = useState('portfolio');
  
  // Ref to track images for cleanup
  const imagesRef = useRef([]);
  
  // Portfolio form data
  const [portfolioData, setPortfolioData] = useState({
    title: '',
    description: '',
    images: [],
    designThemeIds: [],
    designSubTagIds: [],
    designMetadata: {},
    overallCost: '',
    carpetAreaSqft: '',
    estimatedCostPerSqft: '',
    siteLocation: '',
    completedDate: '',
    propertyType: '',
    orderPosition: 0,
  });
  // Inline per-field validation errors. Cleared on the field's next change.
  const [fieldErrors, setFieldErrors] = useState({});
  const clearFieldError = (name) =>
    setFieldErrors((prev) => {
      if (!(name in prev)) return prev;
      const next = { ...prev };
      delete next[name];
      return next;
    });
  // Metadata step: only flag empty required fields after the user has tried
  // to save. Avoids the form looking angry on first render with every input
  // outlined in red.
  const [attemptedMetadataSave, setAttemptedMetadataSave] = useState(false);

  // House sections for image tagging (shared with design submission)
  const houseSections = HOUSE_SECTIONS;

  // Design themes (hardcoded with integer IDs)
  const designThemesList = [
    { id: 1, name: 'Modern' },
    { id: 2, name: 'Minimalistic' },
    { id: 3, name: 'Traditional' },
    { id: 4, name: 'Contemporary' },
  ];

  // Design sub-tags (hardcoded with integer IDs)
  const designSubTagsList = [
    { id: 1, name: 'Sustainable' },
    { id: 2, name: 'Vastu-friendly' },
    { id: 3, name: 'Space Saving' },
    { id: 4, name: 'Kids Friendly' },
    { id: 5, name: 'Budget Friendly' },
    { id: 6, name: 'Pet Friendly' },
    { id: 7, name: 'Premium' },
    { id: 8, name: 'Smart Home' },
    { id: 9, name: 'Elderly Friendly' },
    { id: 10, name: 'WFH Setup' },
  ];

  // Fixed metadata field definitions
  const metadataFields = {
    living_room: {
      'Room Geometry': {
        'Room length (mm)': { type: 'number', placeholder: 'e.g., 4200' },
        'Room width (mm)': { type: 'number', placeholder: 'e.g., 3600' },
        'Ceiling height (mm)': { type: 'number', placeholder: 'e.g., 2700' },
        'Entry position': { type: 'text', placeholder: 'e.g., South-East corner' },
        'Window positions': { type: 'textarea', placeholder: 'e.g., West wall, centered' },
      },
      'Zoning & Layout': {
        'Primary seating zone': { type: 'text', placeholder: 'e.g., Near window' },
        'Circulation path': { type: 'textarea', placeholder: 'e.g., Entry → Sofa → Balcony' },
        'TV wall orientation': { type: 'text', placeholder: 'e.g., North wall' },
      },
      'Sofa': {
        'Sofa type': { type: 'text', placeholder: 'e.g., L-shaped sectional' },
        'Sofa footprint (L×D mm)': { type: 'text', placeholder: 'e.g., 2600 × 1600' },
        'Seat height (mm)': { type: 'number', placeholder: 'e.g., 420' },
        'Upholstery material': { type: 'text', placeholder: 'e.g., Fabric' },
        'Upholstery color': { type: 'text', placeholder: 'e.g., Warm grey' },
        'Backrest style': { type: 'text', placeholder: 'e.g., Low back' },
      },
      'Center Table': {
        'Table type': { type: 'text', placeholder: 'e.g., Rectangular' },
        'Dimensions (mm)': { type: 'text', placeholder: 'e.g., 1200 × 600 × 400' },
        'Material': { type: 'text', placeholder: 'e.g., Solid wood' },
        'Finish': { type: 'text', placeholder: 'e.g., Natural oak' },
      },
      'TV Unit / Storage': {
        'TV panel width (mm)': { type: 'number', placeholder: 'e.g., 2400' },
        'Storage depth (mm)': { type: 'number', placeholder: 'e.g., 450' },
        'Storage type': { type: 'text', placeholder: 'e.g., Base unit + open shelves' },
        'Finish': { type: 'text', placeholder: 'e.g., Walnut veneer' },
      },
      'Lighting': {
        'Ambient lighting': { type: 'text', placeholder: 'e.g., Cove lighting' },
        'Task lighting': { type: 'text', placeholder: 'e.g., Floor lamp near sofa' },
        'Color temperature': { type: 'text', placeholder: 'e.g., 3000K' },
      },
      'Wall Treatments': {
        'Accent wall': { type: 'text', placeholder: 'e.g., Yes (TV wall)' },
        'Accent material': { type: 'text', placeholder: 'e.g., Textured plaster' },
        'Wall color': { type: 'text', placeholder: 'e.g., Warm beige' },
      },
      'Flooring': {
        'Floor material': { type: 'text', placeholder: 'e.g., Porcelain tile' },
        'Tile size': { type: 'text', placeholder: 'e.g., 1200 × 600' },
        'Finish': { type: 'text', placeholder: 'e.g., Matte' },
      },
      'Design Language': {
        'Theme': { type: 'text', placeholder: 'e.g., Industrial-Rustic' },
        'Visual weight': { type: 'text', placeholder: 'e.g., Medium-heavy' },
        'Styling density': { type: 'text', placeholder: 'e.g., Medium' },
      },
      'Décor Rules': {
        'Décor type': { type: 'text', placeholder: 'e.g., Metal + wood' },
        'Décor density': { type: 'text', placeholder: 'e.g., Sparse' },
        'Plants': { type: 'text', placeholder: 'e.g., Yes (1–2 indoor plants)' },
      },
    },
    bedroom: {
      'Room Geometry': {
        'Room size (mm)': { type: 'text', placeholder: 'e.g., 3600 × 4200' },
        'Ceiling height (mm)': { type: 'number', placeholder: 'e.g., 2700' },
        'Window position': { type: 'text', placeholder: 'e.g., East wall' },
      },
      'Bed': {
        'Bed size': { type: 'text', placeholder: 'e.g., King' },
        'Bed footprint (mm)': { type: 'text', placeholder: 'e.g., 1900 × 2100' },
        'Headboard height (mm)': { type: 'number', placeholder: 'e.g., 1200' },
        'Headboard material': { type: 'text', placeholder: 'e.g., Upholstered' },
        'Headboard color': { type: 'text', placeholder: 'e.g., Taupe' },
      },
      'Side Tables': {
        'Quantity': { type: 'number', placeholder: 'e.g., 2' },
        'Size per table (mm)': { type: 'text', placeholder: 'e.g., 450 × 400' },
        'Material': { type: 'text', placeholder: 'e.g., Veneer' },
      },
      'Wardrobe': {
        'Wardrobe type': { type: 'text', placeholder: 'e.g., Sliding' },
        'Width (mm)': { type: 'number', placeholder: 'e.g., 2400' },
        'Depth (mm)': { type: 'number', placeholder: 'e.g., 600' },
        'Height (mm)': { type: 'text', placeholder: 'e.g., Full height' },
        'Internal layout': { type: 'text', placeholder: 'e.g., Hanging + drawers' },
        'Finish': { type: 'text', placeholder: 'e.g., Matte laminate' },
      },
      'Lighting': {
        'Bedside lighting': { type: 'text', placeholder: 'e.g., Pendant' },
        'Wardrobe lighting': { type: 'text', placeholder: 'e.g., Internal LED' },
        'Color temperature': { type: 'text', placeholder: 'e.g., 3000K' },
      },
      'Wall Treatments': {
        'Bed back wall': { type: 'text', placeholder: 'e.g., Panelled' },
        'Material': { type: 'text', placeholder: 'e.g., MDF panels' },
        'Color': { type: 'text', placeholder: 'e.g., Warm grey' },
      },
      'Flooring': {
        'Floor material': { type: 'text', placeholder: 'e.g., Wooden laminate' },
        'Shade': { type: 'text', placeholder: 'e.g., Walnut' },
      },
      'Design Language': {
        'Theme': { type: 'text', placeholder: 'e.g., Modern Indian Minimal' },
        'Mood': { type: 'text', placeholder: 'e.g., Calm, premium' },
      },
      'Décor Rules': {
        'Décor density': { type: 'text', placeholder: 'e.g., Low' },
        'Art placement': { type: 'text', placeholder: 'e.g., Above headboard' },
      },
    },
    kitchen: {
      'Room Geometry': {
        'Kitchen type': { type: 'text', placeholder: 'e.g., L-shaped' },
        'Length A (mm)': { type: 'number', placeholder: 'e.g., 3000' },
        'Length B (mm)': { type: 'number', placeholder: 'e.g., 1800' },
        'Ceiling height (mm)': { type: 'number', placeholder: 'e.g., 2700' },
      },
      'Base Units': {
        'Counter height (mm)': { type: 'number', placeholder: 'e.g., 900' },
        'Depth (mm)': { type: 'number', placeholder: 'e.g., 600' },
        'Shutter finish': { type: 'text', placeholder: 'e.g., Matte laminate' },
        'Shutter color': { type: 'text', placeholder: 'e.g., Warm grey' },
      },
      'Wall Units': {
        'Height (mm)': { type: 'number', placeholder: 'e.g., 750' },
        'Finish': { type: 'text', placeholder: 'e.g., Walnut laminate' },
      },
      'Countertop': {
        'Material': { type: 'text', placeholder: 'e.g., Quartz' },
        'Color': { type: 'text', placeholder: 'e.g., Light beige' },
        'Thickness (mm)': { type: 'number', placeholder: 'e.g., 20' },
      },
      'Backsplash': {
        'Material': { type: 'text', placeholder: 'e.g., Subway tiles' },
        'Color': { type: 'text', placeholder: 'e.g., Off-white' },
      },
      'Appliances': {
        'Hob position': { type: 'text', placeholder: 'e.g., Centered' },
        'Chimney type': { type: 'text', placeholder: 'e.g., Concealed' },
        'Refrigerator space (mm)': { type: 'number', placeholder: 'e.g., 900' },
      },
      'Lighting': {
        'Task lighting': { type: 'text', placeholder: 'e.g., Under-cabinet' },
        'Ambient lighting': { type: 'text', placeholder: 'e.g., Ceiling spots' },
      },
      'Design Language': {
        'Theme': { type: 'text', placeholder: 'e.g., Modern functional' },
        'Visual weight': { type: 'text', placeholder: 'e.g., Light' },
      },
      'Storage Logic': {
        'Tall units': { type: 'text', placeholder: 'e.g., Yes' },
        'Corner solution': { type: 'text', placeholder: 'e.g., Magic corner' },
      },
    },
    bathroom: {
      'Room Geometry': {
        'Bathroom size (mm)': { type: 'text', placeholder: 'e.g., 1500 × 2400' },
      },
      'Wet/Dry Zoning': {
        'Zoning type': { type: 'text', placeholder: 'e.g., Glass partition' },
      },
      'Vanity': {
        'Vanity width (mm)': { type: 'number', placeholder: 'e.g., 900' },
        'Depth (mm)': { type: 'number', placeholder: 'e.g., 450' },
        'Storage type': { type: 'text', placeholder: 'e.g., Drawer + shutter' },
        'Finish': { type: 'text', placeholder: 'e.g., Matte laminate' },
      },
      'Sanitaryware': {
        'WC type': { type: 'text', placeholder: 'e.g., Wall-hung' },
        'Basin type': { type: 'text', placeholder: 'e.g., Countertop' },
      },
      'Shower Area': {
        'Shower type': { type: 'text', placeholder: 'e.g., Overhead + hand' },
        'Partition height (mm)': { type: 'number', placeholder: 'e.g., 2100' },
      },
      'Tiles': {
        'Wall tile size': { type: 'text', placeholder: 'e.g., 600 × 1200' },
        'Floor tile finish': { type: 'text', placeholder: 'e.g., Anti-skid' },
        'Color palette': { type: 'text', placeholder: 'e.g., Grey + beige' },
      },
      'Lighting': {
        'Mirror lighting': { type: 'text', placeholder: 'e.g., Backlit' },
        'Color temperature': { type: 'text', placeholder: 'e.g., Neutral white' },
      },
      'Design Language': {
        'Theme': { type: 'text', placeholder: 'e.g., Contemporary' },
        'Mood': { type: 'text', placeholder: 'e.g., Clean, hotel-like' },
      },
    },
  };

  // Design metadata structure - supports multiple bedrooms/bathrooms and nesting
  // Initialize with one bedroom by default
  const [designMetadata, setDesignMetadata] = useState({
    living_room: {},
    bedrooms: [{ bathrooms: [] }], // Array of bedroom objects, each can have nested bathrooms - start with one bedroom
    kitchen: {},
    bathrooms: [], // Standalone bathrooms
  });

  // State for collapsed/expanded metadata sections
  // Structure: { roomType_index_sectionKey: boolean } for sub-sections
  // Structure: { roomType_index: boolean } for main room sections
  const [collapsedSections, setCollapsedSections] = useState({});
  // Initialize empty - living_room will default to expanded (false), others to collapsed (true)
  const [collapsedRooms, setCollapsedRooms] = useState({});


  // Load existing portfolio when editing
  useEffect(() => {
    if (!isEditMode || !editPortfolioIdParam) return;
    let cancelled = false;

    const loadPortfolioForEdit = async () => {
      setEditInitialLoading(true);
      try {
        const response = await axiosInstance.get('/interior-designers/portfolio');
        const list = Array.isArray(response) ? response : response?.items || [];
        const targetId = String(editPortfolioIdParam);
        const item = list.find((p) => {
          const pid = p?.id ?? p?.interiorDesignerPortfolioItemId ?? p?.portfolioId;
          return pid != null && String(pid) === targetId;
        });
        if (!item) {
          toast.error('Portfolio item not found');
          navigate(designsPortfolioReturnHref());
          return;
        }

        const pid = item.id ?? item.interiorDesignerPortfolioItemId ?? item.portfolioId;
        setCreatedPortfolioId(pid != null ? String(pid) : null);

        const cd = item.completedDate ?? item.completed_date;
        const completedDateStr = cd ? String(cd).split('T')[0] : '';

        setPortfolioData({
          title: item.title || '',
          description: item.description || '',
          images: [],
          designThemeIds: extractNumericIds(item.designThemeIds ?? item.designThemes),
          designSubTagIds: extractNumericIds(item.designSubTagIds ?? item.designSubTags),
          designMetadata: {},
          overallCost:
            item.overallCost != null || item.overall_cost != null
              ? String(item.overallCost ?? item.overall_cost ?? '')
              : '',
          carpetAreaSqft:
            item.carpetAreaSqft != null || item.carpet_area_sqft != null
              ? String(item.carpetAreaSqft ?? item.carpet_area_sqft ?? '')
              : '',
          estimatedCostPerSqft:
            item.estimatedCostPerSqft != null || item.estimated_cost_per_sqft != null
              ? String(item.estimatedCostPerSqft ?? item.estimated_cost_per_sqft ?? '')
              : '',
          siteLocation: item.siteLocation ?? item.site_location ?? '',
          completedDate: completedDateStr,
          propertyType: item.propertyType ?? item.property_type ?? '',
          orderPosition: item.orderPosition ?? item.order_position ?? 0,
        });

        setDesignMetadata(normalizeDesignMetadataForForm(item.designMetadata ?? item.design_metadata));

        const rawImages = item.images || [];
        const mappedForUrls = rawImages.map((img) => {
          if (typeof img === 'string') return { s3_key: img, section: HOUSE_SECTIONS[0] };
          const key = img?.s3_key || img?.s3Key;
          return {
            s3_key: key,
            section: img?.section || HOUSE_SECTIONS[0],
          };
        });
        const s3Keys = mappedForUrls.map((m) => m.s3_key).filter(Boolean);

        let urlMap = {};
        if (s3Keys.length > 0) {
          try {
            const pres = await getPresignedDownloadUrls(s3Keys, axiosInstance);
            pres.forEach((p, i) => {
              const k = s3Keys[i];
              urlMap[k] = p.download_url || p.downloadUrl || p.url;
            });
          } catch (e) {
            console.error('Failed to load image previews for edit:', e);
          }
        }

        const imageRows = mappedForUrls
          .filter((m) => m.s3_key)
          .map((m) => ({
            existing: true,
            s3_key: m.s3_key,
            section: m.section || HOUSE_SECTIONS[0],
            previewUrl: urlMap[m.s3_key] || null,
          }));

        setPortfolioData((prev) => ({ ...prev, images: imageRows }));
        imagesRef.current = imageRows;
      } catch (e) {
        console.error('Error loading portfolio for edit:', e);
        toast.error(e.response?.data?.message || 'Failed to load portfolio');
        navigate(designsPortfolioReturnHref());
      } finally {
        if (!cancelled) setEditInitialLoading(false);
      }
    };

    loadPortfolioForEdit();
    return () => {
      cancelled = true;
    };
  }, [isEditMode, editPortfolioIdParam, navigate]);

  // NOTE: We intentionally do NOT gate portfolio creation on having a paid
  // phase subscription. The backend creates the portfolio↔config↔cost rows in
  // BOTH directions — when a portfolio is created/updated it backfills costs
  // for the designer's existing paid subscriptions, and when a subscription is
  // marked paid it backfills costs for the designer's existing portfolios. So
  // either order works; forcing "subscribe first" was an unnecessary block.

  // Previews are presigned S3 download URLs (not blob: object URLs), so there
  // is nothing to revoke on unmount. Kept intentionally empty for clarity.


  const handlePortfolioInputChange = (field, value) => {
    setPortfolioData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleImageSelect = async (e) => {
    const files = Array.from(e.target.files);
    // Reset the input so re-selecting the same file fires onChange again.
    e.target.value = '';
    const validImages = files.filter(file => file.type.startsWith('image/'));

    if (validImages.length === 0) {
      toast.error('Please select only image files');
      return;
    }

    // NO base64 is ever read. Files go straight to S3 via presigned PUT, and
    // we only ever hold { s3_key, content_type, section, previewUrl } in state.
    setUploadingImages(true);
    try {
      // 1) Presigned PUT URLs. Designers may use the 'portfolio' folder; the
      //    backend resolves it to interior-designers/{id}/portfolio/.
      const presignedData = await getPresignedUploadUrls(validImages, 'portfolio', axiosInstance);

      // 2) Upload the raw File objects to S3.
      const uploadData = presignedData.map((presigned, index) => ({
        file: validImages[index],
        upload_url: presigned.upload_url,
        content_type: presigned.content_type,
      }));
      await uploadFilesToS3(uploadData);

      // 3) Presigned GET URLs for in-place preview (no base64, no blob URLs).
      const newImages = await Promise.all(
        presignedData.map(async (presigned, index) => {
          let previewUrl = null;
          try {
            const dl = await getPresignedDownloadUrl(presigned.s3_key, axiosInstance);
            previewUrl = dl.download_url || dl.downloadUrl || null;
          } catch (err) {
            console.error('Failed to get preview URL for uploaded image:', err);
          }
          return {
            s3_key: presigned.s3_key,
            content_type: presigned.content_type || validImages[index].type,
            section: houseSections[0], // Default to first section
            previewUrl,
          };
        })
      );

      setPortfolioData(prev => {
        const merged = [...prev.images, ...newImages];
        imagesRef.current = merged; // Update ref
        return { ...prev, images: merged };
      });
    } catch (error) {
      console.error('Error uploading images:', error);
      toast.error(error.message || 'Failed to upload images');
    } finally {
      setUploadingImages(false);
    }
  };

  const handleRemoveImage = (index) => {
    setPortfolioData(prev => {
      const newImages = prev.images.filter((_, i) => i !== index);
      imagesRef.current = newImages; // Update ref
      return {
        ...prev,
        images: newImages
      };
    });
  };

  // Resolve a renderable src for a portfolio image row.
  // - Fresh uploads + edit-loaded rows carry `previewUrl` (a presigned S3 GET URL).
  // - Legacy rows may still be raw base64 strings or `{ base64 }` objects — kept
  //   so existing portfolios continue to render. Do NOT remove this branch.
  // - As a last resort, an `{ s3_key }` with no resolved previewUrl returns null
  //   (LoadingImage shows its placeholder).
  const getImageSrc = (image) => {
    if (!image) return null;
    if (typeof image === 'string') return image; // raw base64 / data URL (legacy)
    if (image.previewUrl) return image.previewUrl;
    if (image.preview) return image.preview; // legacy field name
    if (image.base64) return image.base64; // legacy base64 (existing portfolios)
    return null;
  };

  const handleImageSectionChange = (index, section) => {
    setPortfolioData(prev => ({
      ...prev,
      images: prev.images.map((img, i) => 
        i === index ? { ...img, section } : img
      )
    }));
  };

  const handleDesignMetadataChange = (category, field, value, index = null, nestedType = null, nestedIndex = null) => {
    setDesignMetadata(prev => {
      // Nested bathroom inside bedroom: category='bathroom', nestedType='bathroom', index=bedroomIndex
      const isNestedBathroom = category === 'bathroom' && nestedType === 'bathroom' && nestedIndex !== null && index !== null;
      // Bedroom fields: category can be 'bedroom' (from metadataFields) or 'bedrooms'
      const isBedroomUpdate = isNestedBathroom || ((category === 'bedrooms' || category === 'bedroom') && index !== null);
      if (isBedroomUpdate) {
        // Update bedroom or nested bathroom
        const newBedrooms = [...prev.bedrooms];
        if (nestedType === 'bathroom' && nestedIndex !== null) {
          // Update nested bathroom inside bedroom
          newBedrooms[index] = {
            ...newBedrooms[index],
            bathrooms: newBedrooms[index].bathrooms.map((bath, i) => 
              i === nestedIndex ? { ...bath, [field]: value } : bath
            )
          };
        } else {
          // Update bedroom field
          newBedrooms[index] = {
            ...newBedrooms[index],
            [field]: value
          };
        }
        return { ...prev, bedrooms: newBedrooms };
      } else if ((category === 'bathrooms' || category === 'bathroom') && index !== null && !(nestedType === 'bathroom' && nestedIndex !== null)) {
        // Update standalone bathroom (category from metadataFields is 'bathroom', state key is 'bathrooms')
        const newBathrooms = [...prev.bathrooms];
        newBathrooms[index] = {
          ...newBathrooms[index],
          [field]: value
        };
        return { ...prev, bathrooms: newBathrooms };
      } else {
        // Update living_room or kitchen (single objects)
        return {
          ...prev,
          [category]: {
            ...prev[category],
            [field]: value
          }
        };
      }
    });
  };

  const handleAddBedroom = () => {
    setDesignMetadata(prev => ({
      ...prev,
      bedrooms: [...prev.bedrooms, { bathrooms: [] }]
    }));
  };

  const handleRemoveBedroom = (index) => {
    setDesignMetadata(prev => ({
      ...prev,
      bedrooms: prev.bedrooms.filter((_, i) => i !== index)
    }));
  };

  const handleAddBathroom = (bedroomIndex = null) => {
    setDesignMetadata(prev => {
      if (bedroomIndex !== null) {
        // Add nested bathroom to bedroom
        const newBedrooms = [...prev.bedrooms];
        newBedrooms[bedroomIndex] = {
          ...newBedrooms[bedroomIndex],
          bathrooms: [...(newBedrooms[bedroomIndex].bathrooms || []), {}]
        };
        return { ...prev, bedrooms: newBedrooms };
      } else {
        // Add standalone bathroom
        return {
          ...prev,
          bathrooms: [...prev.bathrooms, {}]
        };
      }
    });
  };

  const handleRemoveBathroom = (bedroomIndex, bathroomIndex) => {
    if (bedroomIndex !== null) {
      // Remove nested bathroom
      setDesignMetadata(prev => {
        const newBedrooms = [...prev.bedrooms];
        newBedrooms[bedroomIndex] = {
          ...newBedrooms[bedroomIndex],
          bathrooms: newBedrooms[bedroomIndex].bathrooms.filter((_, i) => i !== bathroomIndex)
        };
        return { ...prev, bedrooms: newBedrooms };
      });
    } else {
      // Remove standalone bathroom
      setDesignMetadata(prev => ({
        ...prev,
        bathrooms: prev.bathrooms.filter((_, i) => i !== bathroomIndex)
      }));
    }
  };

  const handleDesignThemeChange = (themeId, checked) => {
    setPortfolioData(prev => ({
      ...prev,
      designThemeIds: checked
        ? [...prev.designThemeIds, themeId]
        : prev.designThemeIds.filter(id => id !== themeId)
    }));
  };

  const handleDesignSubTagChange = (tagId, checked) => {
    setPortfolioData(prev => ({
      ...prev,
      designSubTagIds: checked
        ? [...prev.designSubTagIds, tagId]
        : prev.designSubTagIds.filter(id => id !== tagId)
    }));
  };

  const validatePortfolioStep = () => {
    // Build a per-field error map so the inputs can highlight + show helper
    // text. Toast still fires for the first issue so users notice the
    // validation triggered.
    const errs = {};
    const title = (portfolioData.title || '').trim();
    if (!title) {
      errs.title = 'Please enter a title';
    } else if (title.length < 3) {
      errs.title = 'Title is too short — use at least 3 characters';
    } else if (title.length > 100) {
      errs.title = 'Title is too long — keep it under 100 characters';
    }

    if (portfolioData.images.length === 0) {
      errs.images = 'Please add at least one image';
    }

    // Numeric guards on optional cost / area fields — non-blocking when
    // empty, but if the user typed something it must be a valid number.
    const numericFields = [
      { key: 'overallCost', label: 'Overall cost' },
      { key: 'carpetAreaSqft', label: 'Carpet area' },
      { key: 'estimatedCostPerSqft', label: 'Estimated cost per sq ft' },
    ];
    for (const f of numericFields) {
      const v = portfolioData[f.key];
      if (v === '' || v == null) continue;
      const n = Number(v);
      if (Number.isNaN(n)) {
        errs[f.key] = `${f.label} must be a number`;
      } else if (n < 0) {
        errs[f.key] = `${f.label} cannot be negative`;
      }
    }

    if (Object.keys(errs).length > 0) {
      setFieldErrors(errs);
      const firstError =
        errs.title ||
        errs.images ||
        errs.overallCost ||
        errs.carpetAreaSqft ||
        errs.estimatedCostPerSqft;
      if (firstError) toast.error(firstError);
      return false;
    }
    setFieldErrors({});
    return true;
  };

  // Helper to check if a value is empty
  const isEmpty = (value) => !value || (typeof value === 'string' && value.trim() === '');

  // Check if all metadata fields are filled (silent check for UI)
  const areAllMetadataFieldsFilled = () => {
    // Validate living room
    if (metadataFields.living_room) {
      // eslint-disable-next-line no-unused-vars
      for (const [section, sectionFields] of Object.entries(metadataFields.living_room)) {
        for (const fieldName of Object.keys(sectionFields)) {
          const fieldKey = fieldName.toLowerCase().replace(/\s+/g, '_');
          const value = designMetadata.living_room?.[fieldKey];
          if (isEmpty(value)) {
            return false;
          }
        }
      }
    }
    
    // Validate bedrooms
    if (designMetadata.bedrooms && designMetadata.bedrooms.length > 0) {
      // eslint-disable-next-line no-unused-vars
      for (const [bedroomIndex, bedroom] of designMetadata.bedrooms.entries()) {
        if (metadataFields.bedroom) {
          // eslint-disable-next-line no-unused-vars
          for (const [section, sectionFields] of Object.entries(metadataFields.bedroom)) {
            for (const fieldName of Object.keys(sectionFields)) {
              const fieldKey = fieldName.toLowerCase().replace(/\s+/g, '_');
              const value = bedroom[fieldKey];
              if (isEmpty(value)) {
                return false;
              }
            }
          }
        }
        
        // Validate nested bathrooms in bedrooms
        if (bedroom.bathrooms && bedroom.bathrooms.length > 0) {
          for (const bathroom of bedroom.bathrooms) {
            if (metadataFields.bathroom) {
              // eslint-disable-next-line no-unused-vars
              for (const [section, sectionFields] of Object.entries(metadataFields.bathroom)) {
                for (const fieldName of Object.keys(sectionFields)) {
                  const fieldKey = fieldName.toLowerCase().replace(/\s+/g, '_');
                  const value = bathroom[fieldKey];
                  if (isEmpty(value)) {
                    return false;
                  }
                }
              }
            }
          }
        }
      }
    }
    
    // Validate kitchen
    if (metadataFields.kitchen) {
      // eslint-disable-next-line no-unused-vars
      for (const [section, sectionFields] of Object.entries(metadataFields.kitchen)) {
        for (const fieldName of Object.keys(sectionFields)) {
          const fieldKey = fieldName.toLowerCase().replace(/\s+/g, '_');
          const value = designMetadata.kitchen?.[fieldKey];
          if (isEmpty(value)) {
            return false;
          }
        }
      }
    }
    
    // Validate standalone bathrooms
    if (designMetadata.bathrooms && designMetadata.bathrooms.length > 0) {
      for (const bathroom of designMetadata.bathrooms) {
        if (metadataFields.bathroom) {
          // eslint-disable-next-line no-unused-vars
          for (const [section, sectionFields] of Object.entries(metadataFields.bathroom)) {
            for (const fieldName of Object.keys(sectionFields)) {
              const fieldKey = fieldName.toLowerCase().replace(/\s+/g, '_');
              const value = bathroom[fieldKey];
              if (isEmpty(value)) {
                return false;
              }
            }
          }
        }
      }
    }
    
    return true;
  };

  // Validate all metadata fields are filled (with error messages)
  const validateAllMetadataFields = () => {
    // Flag the form as "attempted" so empty fields light up red.
    setAttemptedMetadataSave(true);
    const missingFields = [];
    
    // Validate living room
    if (metadataFields.living_room) {
      Object.entries(metadataFields.living_room).forEach(([section, sectionFields]) => {
        Object.keys(sectionFields).forEach(fieldName => {
          const fieldKey = fieldName.toLowerCase().replace(/\s+/g, '_');
          const value = designMetadata.living_room?.[fieldKey];
          if (isEmpty(value)) {
            missingFields.push(`Living Room - ${section} - ${fieldName}`);
          }
        });
      });
    }
    
    // Validate bedrooms
    if (designMetadata.bedrooms && designMetadata.bedrooms.length > 0) {
      designMetadata.bedrooms.forEach((bedroom, bedroomIndex) => {
        if (metadataFields.bedroom) {
          Object.entries(metadataFields.bedroom).forEach(([section, sectionFields]) => {
            Object.keys(sectionFields).forEach(fieldName => {
              const fieldKey = fieldName.toLowerCase().replace(/\s+/g, '_');
              const value = bedroom[fieldKey];
              if (isEmpty(value)) {
                missingFields.push(`Bedroom ${bedroomIndex + 1} - ${section} - ${fieldName}`);
              }
            });
          });
        }
        
        // Validate nested bathrooms in bedrooms
        if (bedroom.bathrooms && bedroom.bathrooms.length > 0) {
          bedroom.bathrooms.forEach((bathroom, bathroomIndex) => {
            if (metadataFields.bathroom) {
              Object.entries(metadataFields.bathroom).forEach(([section, sectionFields]) => {
                Object.keys(sectionFields).forEach(fieldName => {
                  const fieldKey = fieldName.toLowerCase().replace(/\s+/g, '_');
                  const value = bathroom[fieldKey];
                  if (isEmpty(value)) {
                    missingFields.push(`Bedroom ${bedroomIndex + 1} - Bathroom ${bathroomIndex + 1} - ${section} - ${fieldName}`);
                  }
                });
              });
            }
          });
        }
      });
    }
    
    // Validate kitchen
    if (metadataFields.kitchen) {
      Object.entries(metadataFields.kitchen).forEach(([section, sectionFields]) => {
        Object.keys(sectionFields).forEach(fieldName => {
          const fieldKey = fieldName.toLowerCase().replace(/\s+/g, '_');
          const value = designMetadata.kitchen?.[fieldKey];
          if (isEmpty(value)) {
            missingFields.push(`Kitchen - ${section} - ${fieldName}`);
          }
        });
      });
    }
    
    // Validate standalone bathrooms
    if (designMetadata.bathrooms && designMetadata.bathrooms.length > 0) {
      designMetadata.bathrooms.forEach((bathroom, index) => {
        if (metadataFields.bathroom) {
          Object.entries(metadataFields.bathroom).forEach(([section, sectionFields]) => {
            Object.keys(sectionFields).forEach(fieldName => {
              const fieldKey = fieldName.toLowerCase().replace(/\s+/g, '_');
              const value = bathroom[fieldKey];
              if (isEmpty(value)) {
                missingFields.push(`Bathroom ${index + 1} - ${section} - ${fieldName}`);
              }
            });
          });
        }
      });
    }
    
    if (missingFields.length > 0) {
      const count = missingFields.length;
      toast.error(
        `${count} required field${count === 1 ? '' : 's'} missing. First: ${missingFields[0]}`
      );
      return false;
    }

    return true;
  };

  // Handle portfolio step submission - just validate and move to metadata
  const handleSubmit = async () => {
    if (!validatePortfolioStep()) return;
    // Move to metadata step - portfolio will be created after metadata is filled
    setCurrentStep('metadata');
  };

  const savePortfolioWithMetadata = async ({
    requireCompleteMetadata = false,
    navigateToBoqAfterSave = false,
  } = {}) => {
    // validatePortfolioStep() already fires the specific field error toast
    // (e.g. "Please enter a title"). No extra generic toast here — that was
    // showing up as a duplicate.
    if (!validatePortfolioStep()) return false;

    if (requireCompleteMetadata && !validateAllMetadataFields()) {
      return false;
    }

    if (uploadingImages) {
      toast.error('Please wait for images to finish uploading');
      return false;
    }

    try {
      setLoading(true);

      // Images were uploaded to S3 on selection — every row already carries an
      // s3_key (freshly uploaded or loaded for edit). The payload is just the
      // S3 keys + section; the backend stores `images` as-is in JSONB. NO
      // base64 and NO File objects are ever sent here.
      const imagePayload = [];
      for (const img of portfolioData.images) {
        if (img.s3_key) {
          imagePayload.push({
            s3_key: img.s3_key,
            section: img.section || houseSections[0],
          });
        }
      }

      if (imagePayload.length === 0) {
        throw new Error('At least one image is required.');
      }

      const payload = {
        title: portfolioData.title,
        description: portfolioData.description || '',
        images: imagePayload,
        designThemeIds: portfolioData.designThemeIds,
        designSubTagIds: portfolioData.designSubTagIds,
        designMetadata: designMetadata,
        overallCost: portfolioData.overallCost ? parseFloat(portfolioData.overallCost) : 0,
        carpetAreaSqft: portfolioData.carpetAreaSqft ? parseFloat(portfolioData.carpetAreaSqft) : 0,
        estimatedCostPerSqft: portfolioData.estimatedCostPerSqft ? parseFloat(portfolioData.estimatedCostPerSqft) : 0,
        siteLocation: portfolioData.siteLocation || '',
        completedDate: portfolioData.completedDate || null,
        propertyType: portfolioData.propertyType || '',
        orderPosition: portfolioData.orderPosition || 0,
      };

      if (isEditMode && editPortfolioIdParam) {
        // Keep PUT body aligned to backend update schema exactly.
        const updatePayload = {
          title: payload.title,
          description: payload.description,
          images: payload.images,
          designThemeIds: payload.designThemeIds,
          designSubTagIds: payload.designSubTagIds,
          designMetadata: payload.designMetadata,
          overallCost: payload.overallCost,
          estimatedCostPerSqft: payload.estimatedCostPerSqft,
          siteLocation: payload.siteLocation,
          completedDate: payload.completedDate,
          propertyType: payload.propertyType,
          orderPosition: payload.orderPosition,
        };

        await axiosInstance.put(`/interior-designers/portfolio/${editPortfolioIdParam}`, updatePayload);
        toast.success('Portfolio updated successfully');
        if (navigateToBoqAfterSave) {
          const pid = editPortfolioIdParam;
          navigate(`/interior-designer/add-portfolio/${pid}/boq`, {
            state: { portfolioEditReturnPath: `/interior-designer/edit-portfolio/${pid}` },
          });
        }
      } else {
        const response = await axiosInstance.post('/interior-designers/portfolio', payload);
        const newPortfolioId =
          response?.data?.interiorDesignerPortfolioItemId || response?.interiorDesignerPortfolioItemId;

        if (!newPortfolioId) {
          throw new Error('Portfolio ID not returned from server');
        }

        toast.success('Portfolio created successfully with metadata');
        setCreatedPortfolioId(newPortfolioId);
        // Notify NavBar (and other listeners) that portfolio status may have changed
        // so the "Set up portfolio" tag can disappear once the first item exists.
        try {
          window.dispatchEvent(new Event('idPortfolioUpdated'));
        } catch (e) { /* no-op */ }
        if (navigateToBoqAfterSave) {
          navigate(`/interior-designer/add-portfolio/${newPortfolioId}/boq`);
        }
      }

    } catch (error) {
      console.error('Error saving portfolio:', error);
      toast.error(error.response?.data?.message || error.message || 'Failed to save portfolio');
      return false;
    } finally {
      setLoading(false);
    }
    return true;
  };

  // Handle metadata step submission — create or update portfolio with metadata, then BOQ.
  // Metadata is optional: whatever the designer has filled (partial or empty) is saved,
  // so this never blocks on incomplete metadata.
  const handleMetadataSubmit = async () => {
    await savePortfolioWithMetadata({
      requireCompleteMetadata: false,
      navigateToBoqAfterSave: true,
    });
  };

  // Create mode: create the portfolio now and skip the optional Design Metadata and
  // BOQ steps entirely. Both can still be added later by editing the portfolio.
  const handleCreateSkippingOptionalSteps = async () => {
    const saved = await savePortfolioWithMetadata({
      requireCompleteMetadata: false,
      navigateToBoqAfterSave: false,
    });
    if (saved) {
      navigate(designsPortfolioReturnHref());
    }
  };

  // Edit mode: allow saving from portfolio step only (without completing metadata)
  const handleSavePortfolioStep = async () => {
    if (!isEditMode) return;
    const saved = await savePortfolioWithMetadata({
      requireCompleteMetadata: false,
      navigateToBoqAfterSave: false,
    });
    if (saved) {
      setCurrentStep('metadata');
    }
  };

  // Edit mode: allow saving metadata as partial progress
  const handleSaveMetadataStep = async () => {
    if (!isEditMode) return;
    const saved = await savePortfolioWithMetadata({
      requireCompleteMetadata: false,
      navigateToBoqAfterSave: true,
    });
    if (saved) {
      setCurrentStep('boq');
    }
  };

  // Toggle sub-section collapse state (e.g., "Room Geometry", "Sofa")
  const toggleSection = (roomType, sectionKey, index = null) => {
    setCollapsedSections(prev => {
      const key = index !== null ? `${roomType}_${index}_${sectionKey}` : `${roomType}_${sectionKey}`;
      return {
        ...prev,
        [key]: !prev[key]
      };
    });
  };

  // Check if sub-section is collapsed
  const isSectionCollapsed = (roomType, sectionKey, index = null) => {
    const key = index !== null ? `${roomType}_${index}_${sectionKey}` : `${roomType}_${sectionKey}`;
    return collapsedSections[key] ?? false;
  };

  // Toggle main room collapse state (e.g., "Living Room", "Bedroom 1")
  const toggleRoom = (roomType, index = null) => {
    setCollapsedRooms(prev => {
      const key = index !== null ? `${roomType}_${index}` : roomType;
      return {
        ...prev,
        [key]: !prev[key]
      };
    });
  };

  // Check if main room is collapsed
  // Default to collapsed (true) for all rooms except living_room and first bedroom
  const isRoomCollapsed = (roomType, index = null) => {
    const key = index !== null ? `${roomType}_${index}` : roomType;
    // If key exists, return its value
    if (collapsedRooms.hasOwnProperty(key)) {
      return collapsedRooms[key];
    }
    // Default: living_room and first bedroom (index 0) are expanded (false), everything else is collapsed (true)
    if (roomType === 'living_room') {
      return false; // Expanded
    }
    if (roomType === 'bedroom' && index === 0) {
      return false; // First bedroom expanded
    }
    return true; // Everything else collapsed
  };

  // Render metadata fields for a room type in table format
  const renderMetadataFields = (roomType, data, onChange, index = null, nestedType = null, nestedIndex = null) => {
    const fields = metadataFields[roomType];
    if (!fields) return null;

    return (
      <div className="space-y-3">
        {Object.entries(fields).map(([section, sectionFields]) => {
          const sectionKey = section.toLowerCase().replace(/\s+/g, '_');
          const isCollapsed = isSectionCollapsed(roomType, sectionKey, index);
          
          return (
            <div key={section}>
              {/* Collapsible Header */}
              <button
                onClick={() => toggleSection(roomType, sectionKey, index)}
                className="w-full flex items-center gap-2 py-2 text-left hover:opacity-70 transition-opacity"
              >
                {isCollapsed ? (
                  <ChevronRight className="w-4 h-4 text-gray-500" />
                ) : (
                  <ChevronDown className="w-4 h-4 text-gray-500" />
                )}
                <h4 className="text-base font-medium text-gray-700">{section}</h4>
              </button>
              
              {/* Collapsible Content - Table Format */}
              {!isCollapsed && (
                <div 
                  className="pl-4 pb-3 mt-1 overflow-x-auto"
                  onClick={(e) => e.stopPropagation()}
                  onMouseDown={(e) => e.stopPropagation()}
                >
                  <table className="w-full min-w-[500px] border-collapse border border-gray-200">
                    <thead>
                      <tr className="bg-gray-50">
                        <th className="text-left py-3 px-4 text-normal font-semibold text-gray-700 uppercase tracking-wide border-b border-gray-200 w-2/5">
                          Metadata Field
                        </th>
                        <th className="text-left py-3 px-4 text-normal font-semibold text-gray-700 uppercase tracking-wide border-b border-gray-200 w-3/5">
                          Value
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(sectionFields).map(([fieldName, fieldConfig], fieldIndex) => {
                        const fieldKey = fieldName.toLowerCase().replace(/\s+/g, '_');
                        const value = data[fieldKey] || '';
                        const isLastRow = fieldIndex === Object.entries(sectionFields).length - 1;
                        // Field is "missing" only after the user has tried to save.
                        // Before that, leave the form clean.
                        const isEmpty = !value || value.trim() === '';
                        const showError = attemptedMetadataSave && isEmpty;

                        return (
                          <tr
                            key={fieldName}
                            className={!isLastRow ? "border-b border-gray-100" : ""}
                            onClick={(e) => e.stopPropagation()}
                            onMouseDown={(e) => e.stopPropagation()}
                          >
                            <td className="py-3 px-4 align-top bg-white">
                              <label className={`text-sm font-medium block mb-1 ${showError ? 'text-rose-600' : 'text-gray-800'}`}>
                                {fieldName} <span className="text-slate-400 font-normal">(optional)</span>
                              </label>
                              {fieldConfig.placeholder && (
                                <p className="text-xs text-gray-500 font-normal mt-0.5">{fieldConfig.placeholder}</p>
                              )}
                            </td>
                            <td className="py-3 px-4 align-top bg-white">
                              {fieldConfig.type === 'textarea' ? (
                                <textarea
                                  value={value}
                                  onChange={(e) => onChange(roomType, fieldKey, e.target.value, index, nestedType, nestedIndex)}
                                  placeholder={fieldConfig.placeholder}
                                  rows={3}
                                  className={`w-full px-3 py-2 border focus:outline-none focus:ring-1 text-sm resize-none bg-white ${
                                    showError
                                      ? 'border-rose-400 focus:ring-rose-400 focus:border-rose-400'
                                      : 'border-gray-300 focus:ring-gray-900 focus:border-gray-900'
                                  }`}
                                  onClick={(e) => e.stopPropagation()}
                                  onMouseDown={(e) => e.stopPropagation()}
                                  required
                                />
                              ) : (
                                <div
                                  onClick={(e) => e.stopPropagation()}
                                  onMouseDown={(e) => e.stopPropagation()}
                                >
                                  <CustomInput
                                    type={fieldConfig.type}
                                    value={value}
                                    onChange={(e) => onChange(roomType, fieldKey, e.target.value, index, nestedType, nestedIndex)}
                                    placeholder={fieldConfig.placeholder}
                                    className={`text-sm w-full ${showError ? 'border-rose-400' : ''}`}
                                    onClick={(e) => e.stopPropagation()}
                                    onMouseDown={(e) => e.stopPropagation()}
                                    error={showError ? 'Required' : undefined}
                                    required
                                  />
                                </div>
                              )}
                              {showError && fieldConfig.type === 'textarea' && (
                                <p className="mt-1 text-xs text-rose-600">Required</p>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  const renderMetadataStep = () => {
    // "Has data" used to be `Object.keys(room).length > 0` — that flipped
    // rooms to complete the moment a field was touched, even with empty
    // strings, so a brand-new bedroom showed a green check. Now we mirror
    // areAllMetadataFieldsFilled()'s per-room check: every field in every
    // section must be non-empty for a room to count as complete.

    const checkFieldsComplete = (definition, source) => {
      if (!definition || !source) return false;
      for (const [, sectionFields] of Object.entries(definition)) {
        for (const fieldName of Object.keys(sectionFields)) {
          const fieldKey = fieldName.toLowerCase().replace(/\s+/g, '_');
          if (isEmpty(source[fieldKey])) return false;
        }
      }
      return true;
    };

    const isLivingRoomComplete = checkFieldsComplete(
      metadataFields.living_room,
      designMetadata.living_room
    );

    const isBedroomsComplete = (() => {
      const list = designMetadata.bedrooms;
      if (!Array.isArray(list) || list.length === 0) return false;
      return list.every((bedroom) => {
        if (!checkFieldsComplete(metadataFields.bedroom, bedroom)) return false;
        // Nested bathrooms in this bedroom — each must also be fully filled.
        if (Array.isArray(bedroom.bathrooms) && bedroom.bathrooms.length > 0) {
          return bedroom.bathrooms.every((bathroom) =>
            checkFieldsComplete(metadataFields.bathroom, bathroom)
          );
        }
        return true;
      });
    })();

    const isKitchenComplete = checkFieldsComplete(
      metadataFields.kitchen,
      designMetadata.kitchen
    );

    const isBathroomsComplete = (() => {
      const list = designMetadata.bathrooms;
      if (!Array.isArray(list) || list.length === 0) return false;
      return list.every((bathroom) =>
        checkFieldsComplete(metadataFields.bathroom, bathroom)
      );
    })();

    const roomStatus = {
      living_room: isLivingRoomComplete,
      bedrooms: isBedroomsComplete,
      kitchen: isKitchenComplete,
      bathrooms: isBathroomsComplete,
    };

    return (
      <div>
        {/* Room Sub-Stepper Timeline */}
        <div className="mb-6 pb-4 border-b border-gray-200">
          <div className="flex items-center">
            {roomTypes.map((room, index) => {
              const isActive = selectedRoomType === room.id;
              const isCompleted = roomStatus[room.id];
              const isLast = index === roomTypes.length - 1;
              
              return (
                <div key={room.id} className="flex items-center flex-1">
                  <button
                    onClick={() => {
                      setSelectedRoomType(room.id);
                      // Auto-expand the selected room
                      if (isRoomCollapsed(room.id)) {
                        toggleRoom(room.id);
                      }
                    }}
                    className="flex flex-col items-center group"
                  >
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold transition-all duration-200 border-2 ${
                      isActive 
                        ? "bg-flamingo-500 text-white border-flamingo-500 shadow-lg shadow-flamingo-500/50" 
                        : isCompleted
                        ? "bg-green-500 text-white border-green-500"
                        : "bg-white text-gray-600 border-gray-300"
                    }`}>
                      {isCompleted ? (
                        <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      ) : (
                        index + 1
                      )}
                    </div>
                    <span className={`text-sm font-semibold mt-2 transition-colors ${
                      isActive 
                        ? "text-flamingo-600" 
                        : isCompleted
                        ? "text-green-600"
                        : "text-gray-500"
                    }`}>
                      {room.name}
                    </span>
                  </button>
                  {!isLast && (
                    <div className={`flex-1 h-1 mx-4 transition-colors ${
                      isCompleted ? "bg-green-500" : "bg-gray-200"
                    }`} />
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Next / Previous Room navigation within metadata */}
        <div className="mb-6 flex justify-between items-center">
          <OutlineButton
            onClick={() => {
              const currentIdx = roomTypes.findIndex(r => r.id === selectedRoomType);
              if (currentIdx > 0) {
                setSelectedRoomType(roomTypes[currentIdx - 1].id);
              }
            }}
            disabled={roomTypes.findIndex(r => r.id === selectedRoomType) <= 0}
            className="px-4 py-2"
          >
            Previous Room
          </OutlineButton>
          <span className="text-sm text-gray-500">
            {roomTypes.findIndex(r => r.id === selectedRoomType) + 1} of {roomTypes.length}
          </span>
          <PrimaryButton
            onClick={() => {
              const currentIdx = roomTypes.findIndex(r => r.id === selectedRoomType);
              if (currentIdx < roomTypes.length - 1) {
                setSelectedRoomType(roomTypes[currentIdx + 1].id);
              }
            }}
            disabled={roomTypes.findIndex(r => r.id === selectedRoomType) >= roomTypes.length - 1}
            className="px-4 py-2"
          >
            Next Room
          </PrimaryButton>
        </div>

        {/* Room Content - scrollbar on outer container */}
        <div className="overflow-y-auto max-h-[calc(100vh-380px)] space-y-3 pr-2">
          {/* Living Room */}
          {selectedRoomType === 'living_room' && (
            <div>
              <button
                onClick={() => toggleRoom('living_room')}
                className="w-full flex items-center gap-2 py-2 text-left hover:opacity-70 transition-opacity"
              >
                {isRoomCollapsed('living_room') ? (
                  <ChevronRight className="w-4 h-4 text-gray-600" />
                ) : (
                  <ChevronDown className="w-4 h-4 text-gray-600" />
                )}
                <h3 className="text-base font-semibold text-gray-800 uppercase tracking-wide">Living Room</h3>
              </button>
              {!isRoomCollapsed('living_room') && (
                <div className="pl-4 pb-3 space-y-3">
                  {renderMetadataFields('living_room', designMetadata.living_room || {}, handleDesignMetadataChange)}
                </div>
              )}
            </div>
          )}

          {/* Bedrooms */}
          {selectedRoomType === 'bedrooms' && (
            <div
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between py-3 mb-2">
                <h3 className="text-base font-semibold text-gray-800 uppercase tracking-wide">Bedrooms</h3>
                <PrimaryButton
                  onClick={handleAddBedroom}
                  className="flex items-center gap-2 px-4 py-2 text-sm font-medium"
                >
                  <Plus size={16} />
                  Add Bedroom
                </PrimaryButton>
              </div>
              {designMetadata.bedrooms && designMetadata.bedrooms.length > 0 ? (
                <div className="space-y-2">
                  {designMetadata.bedrooms.map((bedroom, bedroomIndex) => {
                    const isBedroomCollapsed = isRoomCollapsed('bedroom', bedroomIndex);
                    return (
                      <div 
                        key={bedroomIndex}
                        onClick={(e) => e.stopPropagation()}
                        onMouseDown={(e) => e.stopPropagation()}
                      >
                        {/* Bedroom Header */}
                        <div className="flex items-center justify-between py-2">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleRoom('bedroom', bedroomIndex);
                            }}
                            className="flex items-center gap-2 text-left hover:opacity-70 transition-opacity"
                          >
                            {isBedroomCollapsed ? (
                              <ChevronRight className="w-4 h-4 text-gray-600" />
                            ) : (
                              <ChevronDown className="w-4 h-4 text-gray-600" />
                            )}
                            <h4 className="text-base font-semibold text-gray-800">
                              Bedroom {bedroomIndex + 1}
                            </h4>
                          </button>
                          <button
                            onClick={() => handleRemoveBedroom(bedroomIndex)}
                            className="text-red-500 hover:text-red-700 flex items-center gap-1 text-xs px-2"
                          >
                            <Trash2 size={14} />
                            Remove
                          </button>
                        </div>
                        
                        {/* Bedroom Content */}
                        {!isBedroomCollapsed && (
                          <div 
                            className="pl-4 pb-3 space-y-3"
                            onClick={(e) => e.stopPropagation()}
                            onMouseDown={(e) => e.stopPropagation()}
                          >
                            {/* Bedroom Fields */}
                            {renderMetadataFields('bedroom', bedroom, handleDesignMetadataChange, bedroomIndex)}
                            
                            {/* Nested Bathrooms */}
                            <div 
                              className="mt-3 pt-3 border-t border-gray-200"
                              onClick={(e) => e.stopPropagation()}
                              onMouseDown={(e) => e.stopPropagation()}
                            >
                              <div className="flex justify-between items-center mb-3">
                                <h5 className="text-base font-semibold text-gray-600 uppercase tracking-wide">Bathrooms</h5>
                                <PrimaryButton
                                  onClick={() => handleAddBathroom(bedroomIndex)}
                                  className="flex items-center gap-2 px-4 py-2 "
                                >
                                  <Plus size={16} />
                                  Add Bathroom
                                </PrimaryButton>
                              </div>
                              {bedroom.bathrooms && bedroom.bathrooms.length > 0 ? (
                                <div className="space-y-2">
                                  {bedroom.bathrooms.map((bathroom, bathroomIndex) => {
                                    const isBathroomCollapsed = isRoomCollapsed('bedroom_bathroom', `${bedroomIndex}_${bathroomIndex}`);
                                    return (
                                      <div 
                                        key={bathroomIndex}
                                        onClick={(e) => e.stopPropagation()}
                                        onMouseDown={(e) => e.stopPropagation()}
                                      >
                                        <div className="flex items-center justify-between py-1">
                                          <button
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              toggleRoom('bedroom_bathroom', `${bedroomIndex}_${bathroomIndex}`);
                                            }}
                                            className="flex items-center gap-2 text-left hover:opacity-70 transition-opacity"
                                          >
                                            {isBathroomCollapsed ? (
                                              <ChevronRight className="w-3 h-3 text-gray-500" />
                                            ) : (
                                              <ChevronDown className="w-3 h-3 text-gray-500" />
                                            )}
                                      <h6 className="text-base font-medium text-gray-600">
                                        Bathroom {bathroomIndex + 1}
                                      </h6>
                                          </button>
                                          <button
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleRemoveBathroom(bedroomIndex, bathroomIndex);
                                            }}
                                            className="text-red-500 hover:text-red-700"
                                          >
                                            <X size={12} />
                                          </button>
                                        </div>
                                        {!isBathroomCollapsed && (
                                          <div 
                                            className="pl-4 pb-3 space-y-3"
                                            onClick={(e) => e.stopPropagation()}
                                            onMouseDown={(e) => e.stopPropagation()}
                                          >
                                            {renderMetadataFields('bathroom', bathroom, handleDesignMetadataChange, bedroomIndex, 'bathroom', bathroomIndex)}
                                          </div>
                                        )}
                                      </div>
                                    );
                                  })}
                                </div>
                              ) : (
                                <p className="text-xs text-gray-400 italic">No bathrooms added</p>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-xs text-gray-400 italic">No bedrooms added</p>
              )}
            </div>
          )}

          {/* Kitchen */}
          {selectedRoomType === 'kitchen' && (
            <div>
              <button
                onClick={() => toggleRoom('kitchen')}
                className="w-full flex items-center gap-2 py-2 text-left hover:opacity-70 transition-opacity"
              >
                {isRoomCollapsed('kitchen') ? (
                  <ChevronRight className="w-4 h-4 text-gray-600" />
                ) : (
                  <ChevronDown className="w-4 h-4 text-gray-600" />
                )}
                <h3 className="text-base font-semibold text-gray-800 uppercase tracking-wide">Kitchen</h3>
              </button>
              {!isRoomCollapsed('kitchen') && (
                <div className="pl-4 pb-3 space-y-3">
                  {renderMetadataFields('kitchen', designMetadata.kitchen || {}, handleDesignMetadataChange)}
                </div>
              )}
            </div>
          )}

          {/* Standalone Bathrooms */}
          {selectedRoomType === 'bathrooms' && (
            <div>
              <div className="flex items-center justify-between py-3 mb-2">
                <h3 className="text-base font-semibold text-gray-800 uppercase tracking-wide">Standalone Bathrooms</h3>
                <PrimaryButton
                  onClick={() => handleAddBathroom(null)}
                  className="flex items-center gap-2 px-4 py-2"
                >
                  <Plus size={16} />
                  Add Bathroom
                </PrimaryButton>
              </div>
              {designMetadata.bathrooms && designMetadata.bathrooms.length > 0 ? (
                <div className="space-y-2">
                  {designMetadata.bathrooms.map((bathroom, index) => {
                    const isBathroomCollapsed = isRoomCollapsed('bathroom', index);
                    return (
                      <div 
                        key={index}
                        onClick={(e) => e.stopPropagation()}
                        onMouseDown={(e) => e.stopPropagation()}
                      >
                        <div className="flex items-center justify-between py-2">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleRoom('bathroom', index);
                            }}
                            className="flex items-center gap-2 text-left hover:opacity-70 transition-opacity"
                          >
                            {isBathroomCollapsed ? (
                              <ChevronRight className="w-4 h-4 text-gray-600" />
                            ) : (
                              <ChevronDown className="w-4 h-4 text-gray-600" />
                            )}
                            <h4 className="text-base font-semibold text-gray-800">
                              Bathroom {index + 1}
                            </h4>
                          </button>
                          <button
                            onClick={() => handleRemoveBathroom(null, index)}
                            className="text-red-500 hover:text-red-700 flex items-center gap-1 text-xs px-2"
                          >
                            <Trash2 size={14} />
                            Remove
                          </button>
                        </div>
                        {!isBathroomCollapsed && (
                          <div 
                            className="pl-4 pb-3 space-y-3"
                            onClick={(e) => e.stopPropagation()}
                            onMouseDown={(e) => e.stopPropagation()}
                          >
                            {renderMetadataFields('bathroom', bathroom, handleDesignMetadataChange, index)}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-xs text-gray-400 italic">No bathrooms added</p>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  // Helper for label markup — keeps the "label + required asterisk" pattern
  // consistent across the portfolio step. Matches the slate-500 label style
  // used by the canonical Field primitive.
  const FieldLabel = ({ children, required = false }) => (
    <label className="block text-sm font-normal text-slate-500 mb-1">
      {children}
      {required && <span className="text-rose-500 ml-1">*</span>}
    </label>
  );

  const renderPortfolioStep = () => (
    <div className="space-y-5">
      <div>
        <FieldLabel required>Title</FieldLabel>
        <CustomInput
          value={portfolioData.title}
          onChange={(e) => {
            handlePortfolioInputChange('title', e.target.value);
            clearFieldError('title');
          }}
          placeholder="Enter portfolio title"
          error={fieldErrors.title}
        />
        {/* CustomInput renders its own error message — no duplicate <p>. */}
      </div>

      <div>
        <FieldLabel>Description</FieldLabel>
        <textarea
          value={portfolioData.description}
          onChange={(e) => handlePortfolioInputChange('description', e.target.value)}
          placeholder="Enter description"
          rows={4}
          className="w-full px-3 py-2 text-sm border border-slate-300 rounded-md bg-white focus:outline-none focus:border-flamingo-500 focus:ring-1 focus:ring-flamingo-200"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <FieldLabel>Overall cost</FieldLabel>
          <CustomInput
            type="number"
            step="0.01"
            value={portfolioData.overallCost}
            onChange={(e) => {
              handlePortfolioInputChange('overallCost', e.target.value);
              clearFieldError('overallCost');
            }}
            placeholder="Enter overall cost"
            error={fieldErrors.overallCost}
          />
        </div>

        <div>
          <FieldLabel>Carpet area (sq ft)</FieldLabel>
          <CustomInput
            type="number"
            step="0.01"
            value={portfolioData.carpetAreaSqft}
            onChange={(e) => {
              handlePortfolioInputChange('carpetAreaSqft', e.target.value);
              clearFieldError('carpetAreaSqft');
            }}
            placeholder="Enter carpet area"
            error={fieldErrors.carpetAreaSqft}
          />
        </div>

        <div>
          <FieldLabel>Estimated cost per sq ft</FieldLabel>
          <CustomInput
            type="number"
            step="0.01"
            value={portfolioData.estimatedCostPerSqft}
            onChange={(e) => {
              handlePortfolioInputChange('estimatedCostPerSqft', e.target.value);
              clearFieldError('estimatedCostPerSqft');
            }}
            placeholder="Enter cost per sq ft"
            error={fieldErrors.estimatedCostPerSqft}
          />
        </div>

        <div>
          <FieldLabel>Site location</FieldLabel>
          <CustomInput
            value={portfolioData.siteLocation}
            onChange={(e) => handlePortfolioInputChange('siteLocation', e.target.value)}
            placeholder="Enter site location"
          />
        </div>

        <div>
          <FieldLabel>Completed date</FieldLabel>
          <CustomInput
            type="date"
            value={portfolioData.completedDate}
            onChange={(e) => handlePortfolioInputChange('completedDate', e.target.value)}
          />
        </div>

        <div>
          <FieldLabel>Property type</FieldLabel>
          <CustomInput
            value={portfolioData.propertyType}
            onChange={(e) => handlePortfolioInputChange('propertyType', e.target.value)}
            placeholder="e.g., 3BHK, 2BHK"
          />
        </div>
      </div>

      <div>
        <FieldLabel>Design themes</FieldLabel>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-1">
          {designThemesList.map((theme) => {
            const selected = portfolioData.designThemeIds.includes(theme.id);
            return (
              <label
                key={theme.id}
                className={`flex items-center gap-2 cursor-pointer px-3 py-2 rounded-lg border transition-colors ${
                  selected
                    ? 'border-flamingo-300 bg-flamingo-50/50 text-flamingo-700'
                    : 'border-slate-200 hover:border-slate-300 text-slate-700'
                }`}
              >
                <input
                  type="checkbox"
                  checked={selected}
                  onChange={(e) => handleDesignThemeChange(theme.id, e.target.checked)}
                  className="rounded border-slate-300 text-flamingo-500 focus:ring-flamingo-500"
                />
                <span className="text-sm font-medium">{theme.name}</span>
              </label>
            );
          })}
        </div>
      </div>

      <div>
        <FieldLabel>Design sub-tags</FieldLabel>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mt-1">
          {designSubTagsList.map((tag) => {
            const selected = portfolioData.designSubTagIds.includes(tag.id);
            return (
              <label
                key={tag.id}
                className={`flex items-center gap-2 cursor-pointer px-3 py-2 rounded-lg border transition-colors ${
                  selected
                    ? 'border-flamingo-300 bg-flamingo-50/50 text-flamingo-700'
                    : 'border-slate-200 hover:border-slate-300 text-slate-700'
                }`}
              >
                <input
                  type="checkbox"
                  checked={selected}
                  onChange={(e) => handleDesignSubTagChange(tag.id, e.target.checked)}
                  className="rounded border-slate-300 text-flamingo-500 focus:ring-flamingo-500"
                />
                <span className="text-sm font-medium">{tag.name}</span>
              </label>
            );
          })}
        </div>
      </div>

      <div>
        <FieldLabel required>Images</FieldLabel>
        <input
          type="file"
          accept="image/*"
          multiple
          disabled={uploadingImages}
          onChange={(e) => {
            handleImageSelect(e);
            clearFieldError('images');
          }}
          className="hidden"
          id="portfolio-images"
        />
        <label
          htmlFor="portfolio-images"
          className={`block border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-colors ${
            fieldErrors.images
              ? 'border-rose-400 bg-rose-50/40 hover:border-rose-500'
              : 'border-slate-300 hover:border-flamingo-400 hover:bg-flamingo-50/20'
          }`}
        >
          <Upload
            size={24}
            className={`mx-auto mb-2 ${fieldErrors.images ? 'text-rose-400' : 'text-slate-400'}`}
          />
          <p className="text-sm text-slate-600">
            {uploadingImages ? 'Uploading…' : 'Click to upload images'}
          </p>
          <p className="text-xs text-slate-400 mt-1">At least 1 image required</p>
        </label>
        {uploadingImages && (
          <p className="mt-1 text-xs text-slate-500 flex items-center gap-2">
            <span className="inline-block w-3 h-3 rounded-full border-2 border-flamingo-500 border-t-transparent animate-spin" />
            Uploading images to secure storage…
          </p>
        )}
        {fieldErrors.images && (
          <p className="mt-1 text-xs text-rose-600">{fieldErrors.images}</p>
        )}

        {portfolioData.images.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
            {portfolioData.images.map((image, index) => {
              // Preview via presigned S3 URL (or legacy base64 for old portfolios).
              const imageSrc = getImageSrc(image);

              return (
                <div key={index} className="relative group border border-gray-200 p-3">
                  <div className="aspect-square overflow-hidden border border-gray-200 mb-2">
                    <LoadingImage
                      src={imageSrc}
                      alt={`Portfolio ${index + 1}`}
                      containerClassName="w-full h-full"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="space-y-2">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Section</label>
                      <select
                        value={image.section || houseSections[0]}
                        onChange={(e) => handleImageSectionChange(index, e.target.value)}
                        className="w-full px-2 py-1.5 text-sm border border-gray-300 focus:outline-none focus:ring-2 focus:ring-flamingo-500"
                      >
                        {houseSections.map((section) => (
                          <option key={section} value={section}>
                            {section}
                          </option>
                        ))}
                      </select>
                    </div>
                    <button
                      onClick={() => handleRemoveImage(index)}
                      className="w-full flex items-center justify-center gap-2 px-3 py-1.5 text-sm text-red-600 hover:text-red-700 hover:bg-red-50 transition-colors"
                    >
                      <X size={14} />
                      Remove
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

    </div>
  );


  // Loading: edit waits only for the portfolio fetch. Create has nothing to
  // pre-check (no subscription gate), so it renders the form immediately.
  if (isEditMode && editInitialLoading) {
    return (
      <div className="p-4 sm:p-6 lg:p-8 flex flex-col gap-6 min-h-[calc(100vh-4rem)] bg-slate-50/40">
        <button
          type="button"
          onClick={() => navigate(designsPortfolioReturnHref())}
          className="self-start inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900 font-medium"
        >
          <ArrowLeft size={16} />
          Back to My Designs
        </button>
        <div className="rounded-2xl shadow-sm bg-gradient-to-r from-slate-800 via-slate-700 to-slate-600 p-5 lg:p-6 text-white">
          <p className="text-xs uppercase tracking-wider text-slate-300">Studio</p>
          <h1 className="text-xl sm:text-2xl font-semibold tracking-tight mt-1">{pageTitle}</h1>
          <p className="text-xs text-slate-300 mt-1">{pageSubtitle}</p>
        </div>
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-12 flex flex-col items-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-flamingo-500 mb-4" />
          <p className="text-sm text-slate-600">Loading portfolio…</p>
        </div>
      </div>
    );
  }

  const steps = [
    { id: 'portfolio', name: 'Portfolio Details', number: 1 },
    { id: 'metadata', name: 'Design Metadata', number: 2 },
    { id: 'boq', name: 'Bill of Quantities', number: 3 },
  ];

  // Room types for sub-stepper
  const roomTypes = [
    { id: 'living_room', name: 'Living Room' },
    { id: 'bedrooms', name: 'Bedrooms' },
    { id: 'kitchen', name: 'Kitchen' },
    { id: 'bathrooms', name: 'Bathrooms' },
  ];

  return (
    <div className="p-4 sm:p-6 lg:p-8 flex flex-col gap-6 min-h-[calc(100vh-4rem)] bg-slate-50/40">
      {/* Back link */}
      <button
        type="button"
        onClick={() => navigate(designsPortfolioReturnHref())}
        className="self-start inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900 font-medium"
      >
        <ArrowLeft size={16} />
        Back to My Designs
      </button>

      {/* Hero band — same family as the rest of the app. */}
      <div className="rounded-2xl shadow-sm bg-gradient-to-r from-slate-800 via-slate-700 to-slate-600 p-5 lg:p-6 text-white">
        <p className="text-xs uppercase tracking-wider text-slate-300">Studio</p>
        <h1 className="text-xl sm:text-2xl font-semibold tracking-tight mt-1">{pageTitle}</h1>
        <p className="text-xs text-slate-300 mt-1">{pageSubtitle}</p>
      </div>

      {/* Horizontal pill stepper — each step shows label, number badge, and
          completion state (check icon when behind the current step). */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-2 inline-flex gap-1 self-start flex-wrap">
        {steps.map((step) => {
          const stepOrder = ['portfolio', 'metadata', 'boq'];
          const isActive = currentStep === step.id;
          const currentStepIndex = stepOrder.indexOf(currentStep);
          const stepIndex = stepOrder.indexOf(step.id);
          const isCompleted = stepIndex < currentStepIndex;
          return (
            <button
              key={step.id}
              type="button"
              onClick={() => {
                // No-op when already on this step.
                if (step.id === currentStep) return;

                // BOQ requires a created portfolio (edit mode loads an
                // existing id, create mode needs the metadata save to run).
                if (step.id === 'boq' && !createdPortfolioId) {
                  toast.error('Please complete portfolio and metadata steps first');
                  return;
                }
                if (step.id === 'boq' && createdPortfolioId) {
                  navigate(`/interior-designer/add-portfolio/${createdPortfolioId}/boq`, {
                    state: isEditMode
                      ? {
                          portfolioEditReturnPath: `/interior-designer/edit-portfolio/${createdPortfolioId}`,
                        }
                      : undefined,
                  });
                  return;
                }

                // Forward navigation from portfolio → metadata: require the
                // portfolio step to validate first. Backward navigation
                // (metadata → portfolio) is always allowed.
                const goingForward = stepIndex > currentStepIndex;
                if (goingForward && currentStep === 'portfolio' && step.id === 'metadata') {
                  if (!validatePortfolioStep()) return;
                }

                setCurrentStep(step.id);
              }}
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-colors ${
                isActive
                  ? 'bg-gradient-to-r from-flamingo-500 to-flamingo-600 text-white shadow-sm'
                  : isCompleted
                  ? 'text-slate-700 hover:bg-slate-50'
                  : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
              }`}
            >
              <span
                className={`w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-semibold ${
                  isActive
                    ? 'bg-white/20 text-white'
                    : isCompleted
                    ? 'bg-emerald-500 text-white'
                    : 'bg-slate-100 text-slate-600'
                }`}
              >
                {isCompleted ? (
                  <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20" aria-hidden>
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                ) : (
                  step.number
                )}
              </span>
              {step.name}
            </button>
          );
        })}
      </div>

      {/* Form content card */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden flex-1 flex flex-col">
        <div className="p-4 sm:p-5 lg:p-6 flex-1">
          {currentStep === 'portfolio' && renderPortfolioStep()}
          {currentStep === 'metadata' && renderMetadataStep()}
        </div>

        {/* Sticky footer — compact action row matching the rest of the app */}
        <div className="border-t border-slate-200 px-4 sm:px-6 py-3 flex justify-between items-center bg-white">
          <OutlineButton
            onClick={() => navigate(designsPortfolioReturnHref())}
            disabled={loading}
            className="!w-auto !px-3 !py-1.5 text-sm"
          >
            Cancel
          </OutlineButton>
          <div className="flex gap-2 flex-wrap justify-end">
            {currentStep === 'metadata' && (
              <OutlineButton
                onClick={() => setCurrentStep('portfolio')}
                className="!w-auto !px-3 !py-1.5 text-sm"
              >
                Previous
              </OutlineButton>
            )}
            {currentStep === 'boq' && (
              <OutlineButton
                onClick={() => {
                  if (createdPortfolioId) {
                    navigate(`/interior-designer/add-portfolio/${createdPortfolioId}/boq`);
                  } else {
                    setCurrentStep('metadata');
                  }
                }}
                className="!w-auto !px-3 !py-1.5 text-sm"
              >
                Previous
              </OutlineButton>
            )}
            {currentStep === 'portfolio' ? (
              isEditMode ? (
                <PrimaryButton
                  onClick={handleSavePortfolioStep}
                  disabled={loading || uploadingImages}
                  className="!w-auto !px-3 !py-1.5 text-sm"
                >
                  {uploadingImages ? 'Uploading…' : loading ? 'Updating…' : 'Update'}
                </PrimaryButton>
              ) : (
                <>
                  <OutlineButton
                    onClick={handleCreateSkippingOptionalSteps}
                    disabled={loading || uploadingImages}
                    className="!w-auto !px-3 !py-1.5 text-sm"
                  >
                    {uploadingImages ? 'Uploading…' : loading ? 'Saving…' : 'Skip & create'}
                  </OutlineButton>
                  <PrimaryButton
                    onClick={handleSubmit}
                    disabled={loading || uploadingImages}
                    className="!w-auto !px-3 !py-1.5 text-sm"
                  >
                    {uploadingImages ? 'Uploading…' : 'Next: Design metadata'}
                  </PrimaryButton>
                </>
              )
            ) : currentStep === 'metadata' ? (
              isEditMode ? (
                <PrimaryButton
                  onClick={handleSaveMetadataStep}
                  disabled={loading || uploadingImages}
                  className="!w-auto !px-3 !py-1.5 text-sm"
                >
                  {uploadingImages ? 'Uploading…' : loading ? 'Updating…' : 'Update'}
                </PrimaryButton>
              ) : (
                <PrimaryButton
                  onClick={handleMetadataSubmit}
                  disabled={loading || uploadingImages}
                  className="!w-auto !px-3 !py-1.5 text-sm"
                >
                  {uploadingImages ? 'Uploading…' : loading ? 'Creating portfolio…' : 'Create portfolio & add BOQ'}
                </PrimaryButton>
              )
            ) : (
              <PrimaryButton
                onClick={() => navigate(designsPortfolioReturnHref())}
                className="!w-auto !px-3 !py-1.5 text-sm"
              >
                Complete
              </PrimaryButton>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddPortfolio;